/**
 * FurniturePanel – rechte Sidebar für den Möbelkatalog.
 *
 * Features:
 * - Katalog nach Gruppen gegliedert (Tabs)
 * - Klick auf Kachel → Möbel zum Platzieren vormerken
 * - PNG hochladen + Metadaten eingeben → in MediaService + ObjectService speichern
 * - Ausgewähltes Möbel auf der Karte: Löschen-Button
 */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useFurnitureStore, CatalogItem } from '../../model/stores/furnitureStore';
import { useAuthStore } from '../../model/stores/authStore';
import { getJwtUserId, deleteMedia, MediaFile } from '../../services/objectClient';
import {
  uploadCatalogItem, registerCatalogItem, listOrphanedMedia,
  deleteItem, deleteCatalogItem, deleteCatalogItemWithMedia, updateCatalogItem,
  resizeItem, rotateItem,
} from '../../services/furnitureService';

const PRESET_GROUPS = ['Arbeitsplätze', 'Sitzgelegenheiten', 'Boards', 'Dekoration', 'Sonstiges'];

// ── Styles ────────────────────────────────────────────────────────────────────

const panel: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: 280,
  zIndex: 300,
  background: 'rgba(10,10,15,0.96)',
  borderLeft: '1px solid rgba(255,255,255,0.1)',
  backdropFilter: 'blur(12px)',
  display: 'flex',
  flexDirection: 'column',
  color: '#fff',
  fontFamily: 'inherit',
};

const header: React.CSSProperties = {
  padding: '14px 16px 10px',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
};

const sectionTitle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'rgba(255,255,255,0.35)',
  padding: '8px 16px 4px',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 8,
  padding: '4px 12px 12px',
};

const tileStyle = (active: boolean): React.CSSProperties => ({
  background: active ? 'rgba(79,142,247,0.25)' : 'rgba(255,255,255,0.05)',
  border: `1px solid ${active ? 'rgba(99,179,237,0.7)' : 'rgba(255,255,255,0.1)'}`,
  borderRadius: 8,
  padding: 6,
  cursor: 'pointer',
  textAlign: 'center',
  fontSize: 10,
  color: 'rgba(255,255,255,0.7)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  transition: 'border-color 0.15s, background 0.15s',
});

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: '#1e2433',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 6,
  padding: '6px 10px',
  color: '#fff',
  fontSize: 12,
  boxSizing: 'border-box',
};

const btnStyle = (color = 'rgba(79,142,247,0.8)'): React.CSSProperties => ({
  background: color,
  border: 'none',
  borderRadius: 6,
  padding: '7px 0',
  color: '#fff',
  fontWeight: 600,
  fontSize: 12,
  cursor: 'pointer',
  width: '100%',
});

// ── Upload-Formular ───────────────────────────────────────────────────────────

const UploadForm: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file,           setFile]           = useState<File | null>(null);
  const [selectedOrphan, setSelectedOrphan] = useState<MediaFile | null>(null);
  const [orphans,        setOrphans]        = useState<MediaFile[]>([]);
  const [loadingOrphans, setLoadingOrphans] = useState(true);
  const [name,    setName]    = useState('');
  const [group,   setGroup]   = useState('Sonstiges');
  const [customG, setCustomG] = useState('');
  const [w,       setW]       = useState(2);
  const [h,       setH]       = useState(2);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');

  const effectiveGroup = customG.trim() || group;

  useEffect(() => {
    listOrphanedMedia()
      .then(setOrphans)
      .catch(() => setOrphans([]))
      .finally(() => setLoadingOrphans(false));
  }, []);

  const handleSelectOrphan = (m: MediaFile) => {
    setSelectedOrphan((prev) => prev?.id === m.id ? null : m);
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDeleteOrphan = async (m: MediaFile) => {
    await deleteMedia(m.id);
    setOrphans((prev) => prev.filter((o) => o.id !== m.id));
    if (selectedOrphan?.id === m.id) setSelectedOrphan(null);
  };

  const handleSubmit = useCallback(async () => {
    if (!selectedOrphan && !file) { setError('Datei auswählen oder ein herrenloses Bild wählen'); return; }
    if (!name.trim()) { setError('Name ist Pflicht'); return; }
    setBusy(true);
    setError('');
    try {
      if (selectedOrphan) {
        await registerCatalogItem(selectedOrphan.url, name.trim(), effectiveGroup, effectiveGroup, w, h);
      } else {
        await uploadCatalogItem(file!, name.trim(), effectiveGroup, effectiveGroup, w, h);
      }
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg.replace(/^Error:\s*/, ''));
    } finally {
      setBusy(false);
    }
  }, [file, selectedOrphan, name, effectiveGroup, w, h, onDone]);

  return (
    <div style={{ padding: '8px 12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Datei-Auswahl */}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) { setFile(f); setSelectedOrphan(null); }
        }}
      />
      <button style={btnStyle(selectedOrphan ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)')}
        onClick={() => fileRef.current?.click()}>
        {file ? `📎 ${file.name}` : '📎 PNG auswählen'}
      </button>

      {/* Herrenlose Bilder */}
      {(loadingOrphans || orphans.length > 0) && (
        <div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {loadingOrphans ? 'Lade herrenlose Bilder…' : `Herrenlose Bilder (${orphans.length})`}
          </div>
          {!loadingOrphans && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {orphans.map((m) => {
                const active = selectedOrphan?.id === m.id;
                return (
                  <div key={m.id} style={{ position: 'relative', width: 60, flexShrink: 0 }}>
                    <img
                      src={m.url}
                      alt={m.name ?? m.id}
                      title={m.name ?? m.url}
                      onClick={() => handleSelectOrphan(m)}
                      style={{
                        width: 60, height: 60, objectFit: 'contain', borderRadius: 6,
                        border: `2px solid ${active ? 'rgba(99,179,237,0.9)' : 'rgba(255,255,255,0.15)'}`,
                        background: active ? 'rgba(79,142,247,0.2)' : 'rgba(255,255,255,0.05)',
                        cursor: 'pointer', boxSizing: 'border-box',
                      }}
                    />
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteOrphan(m); }}
                      title="Bild löschen"
                      style={{
                        position: 'absolute', top: -5, right: -5,
                        width: 18, height: 18, borderRadius: '50%',
                        background: 'rgba(239,68,68,0.9)', border: 'none',
                        color: '#fff', fontSize: 10, fontWeight: 700,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        lineHeight: 1,
                      }}
                    >✕</button>
                  </div>
                );
              })}
            </div>
          )}
          {selectedOrphan && (
            <div style={{ fontSize: 10, color: 'rgba(99,179,237,0.8)', marginTop: 4 }}>
              Ausgewählt: {selectedOrphan.name ?? selectedOrphan.url.split('/').pop()} — kein erneuter Upload nötig.
            </div>
          )}
        </div>
      )}

      {/* Name */}
      <input style={inputStyle} placeholder="Name *" value={name}
        onChange={(e) => setName(e.target.value)} />

      {/* Gruppe */}
      <select style={inputStyle} value={group} onChange={(e) => setGroup(e.target.value)}>
        {PRESET_GROUPS.map((g) => (
          <option key={g} value={g} style={{ background: '#1e2433', color: '#fff' }}>{g}</option>
        ))}
      </select>
      <input style={inputStyle} placeholder="Eigene Gruppe (optional)" value={customG}
        onChange={(e) => setCustomG(e.target.value)} />

      {/* Standardgröße */}
      <div style={{ display: 'flex', gap: 6 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Breite (Tiles)</label>
          <input style={inputStyle} type="number" min={0.5} max={20} step={0.5}
            value={w} onChange={(e) => setW(Number(e.target.value))} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Höhe (Tiles)</label>
          <input style={inputStyle} type="number" min={0.5} max={20} step={0.5}
            value={h} onChange={(e) => setH(Number(e.target.value))} />
        </div>
      </div>

      {error && <div style={{ color: '#f87171', fontSize: 11 }}>{error}</div>}
      <button style={btnStyle(busy ? 'rgba(79,142,247,0.4)' : undefined)}
        onClick={handleSubmit} disabled={busy}>
        {busy ? (selectedOrphan ? 'Speichert…' : 'Lädt hoch…') : (selectedOrphan ? '💾 Als Möbel speichern' : '⬆ Hochladen')}
      </button>
      <button style={btnStyle('rgba(255,255,255,0.06)')} onClick={onDone}>
        Abbrechen
      </button>
    </div>
  );
};

// ── Katalog-Item bearbeiten ───────────────────────────────────────────────────

const EditCatalogForm: React.FC<{ item: CatalogItem; onDone: () => void }> = ({ item, onDone }) => {
  const [name,    setName]    = useState(item.name);
  const [group,   setGroup]   = useState(
    PRESET_GROUPS.includes(item.group) ? item.group : 'Sonstiges',
  );
  const [customG, setCustomG] = useState(
    PRESET_GROUPS.includes(item.group) ? '' : item.group,
  );
  const [w, setW] = useState(item.defaultWidth);
  const [h, setH] = useState(item.defaultHeight);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const effectiveGroup = customG.trim() || group;

  const handleSave = useCallback(async () => {
    if (!name.trim()) { setError('Name darf nicht leer sein'); return; }
    setBusy(true);
    setError('');
    try {
      await updateCatalogItem(item.id, { name: name.trim(), group: effectiveGroup, defaultWidth: w, defaultHeight: h });
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg.replace(/^Error:\s*/, ''));
    } finally {
      setBusy(false);
    }
  }, [item.id, name, effectiveGroup, w, h, onDone]);

  const handleDelete = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      await deleteCatalogItemWithMedia(item.id);
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg.replace(/^Error:\s*/, ''));
      setBusy(false);
    }
  }, [item.id, onDone]);

  return (
    <div style={{ padding: '8px 12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={sectionTitle}>✏ Bearbeiten</div>
      <input style={inputStyle} placeholder="Name *" value={name}
        onChange={(e) => setName(e.target.value)} />
      <select style={inputStyle} value={group} onChange={(e) => setGroup(e.target.value)}>
        {PRESET_GROUPS.map((g) => (
          <option key={g} value={g} style={{ background: '#1e2433', color: '#fff' }}>{g}</option>
        ))}
      </select>
      <input style={inputStyle} placeholder="Eigene Gruppe (optional)" value={customG}
        onChange={(e) => setCustomG(e.target.value)} />
      <div style={{ display: 'flex', gap: 6 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Breite (Tiles)</label>
          <input style={inputStyle} type="number" min={0.5} max={20} step={0.5}
            value={w} onChange={(e) => setW(Number(e.target.value))} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Höhe (Tiles)</label>
          <input style={inputStyle} type="number" min={0.5} max={20} step={0.5}
            value={h} onChange={(e) => setH(Number(e.target.value))} />
        </div>
      </div>
      {error && <div style={{ color: '#f87171', fontSize: 11 }}>{error}</div>}
      <button style={btnStyle(busy ? 'rgba(79,142,247,0.4)' : undefined)}
        onClick={handleSave} disabled={busy}>
        {busy ? 'Speichert...' : '💾 Speichern'}
      </button>
      {confirmDelete ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={{ ...btnStyle('rgba(239,68,68,0.85)'), flex: 1 }}
            onClick={handleDelete} disabled={busy}>
            {busy ? 'Löscht...' : 'Ja, löschen'}
          </button>
          <button style={{ ...btnStyle('rgba(255,255,255,0.08)'), flex: 1 }}
            onClick={() => setConfirmDelete(false)} disabled={busy}>
            Abbrechen
          </button>
        </div>
      ) : (
        <button style={btnStyle('rgba(239,68,68,0.6)')}
          onClick={() => setConfirmDelete(true)} disabled={busy}>
          🗑 Aus Katalog löschen (+ Bild)
        </button>
      )}
      <button style={btnStyle('rgba(255,255,255,0.06)')} onClick={onDone} disabled={busy}>Abbrechen</button>
    </div>
  );
};

// ── Hauptpanel ────────────────────────────────────────────────────────────────

interface Props {
  onClose: () => void;
}

const FurniturePanel: React.FC<Props> = ({ onClose }) => {
  const { catalogItems, pendingCatalogItem, selectedId, placedItems,
          setPendingCatalogItem, selectItem } = useFurnitureStore();
  const jwt    = useAuthStore((s) => s.jwt);
  const isAuth = !!jwt;
  const ownerId    = getJwtUserId();

  const [activeGroup,  setActiveGroup]  = useState<string>('Alle');
  const [showUpload,   setShowUpload]   = useState(false);
  const [editingItem,  setEditingItem]  = useState<CatalogItem | null>(null);

  // Alle vorhandenen Gruppen aus dem Katalog + Presets
  const groups = ['Alle', ...Array.from(new Set([
    ...PRESET_GROUPS,
    ...catalogItems.map((i) => i.group),
  ]))];

  const filtered = activeGroup === 'Alle'
    ? catalogItems
    : catalogItems.filter((i) => i.group === activeGroup);

  const selectedPlaced = selectedId ? placedItems.find((i) => i.id === selectedId) : null;
  const canDeleteSelected = selectedPlaced &&
    (selectedPlaced.ownerId === ownerId /* admin check via store */);

  const handleSelectCatalog = (item: CatalogItem) => {
    if (!isAuth) return;
    setPendingCatalogItem(pendingCatalogItem?.id === item.id ? null : item);
  };

  const handleDeleteSelected = async () => {
    if (!selectedId) return;
    await deleteItem(selectedId);
    selectItem(null);
  };

  return (
    <div style={panel}>
      {/* Header */}
      <div style={header}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>🪑 Möbelkatalog</span>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
          cursor: 'pointer', fontSize: 18, lineHeight: 1,
        }}>✕</button>
      </div>

      {!isAuth && (
        <div style={{ padding: '12px 16px', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
          Einloggen um Möbel zu platzieren.
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Auswahl-Info */}
        {pendingCatalogItem && (
          <div style={{
            margin: '8px 12px', padding: '8px 12px',
            background: 'rgba(79,142,247,0.15)',
            border: '1px solid rgba(99,179,237,0.5)',
            borderRadius: 8, fontSize: 12,
          }}>
            <b>{pendingCatalogItem.name}</b> auswählen → auf Karte klicken um zu platzieren.
            <br />
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>ESC zum Abbrechen</span>
          </div>
        )}

        {/* Ausgewähltes Möbel auf der Karte */}
        {selectedPlaced && (
          <div style={{
            margin: '8px 12px', padding: '8px 12px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8, fontSize: 12,
          }}>
            <div style={{ marginBottom: 10, color: 'rgba(255,255,255,0.6)' }}>
              Ausgewählt: <b style={{ color: '#fff' }}>{selectedPlaced.type}</b>
            </div>

            {/* Größensteuerung */}
            {[
              { label: 'Breite', val: selectedPlaced.width,  other: selectedPlaced.height, isW: true  },
              { label: 'Höhe',   val: selectedPlaced.height, other: selectedPlaced.width,  isW: false },
            ].map(({ label, val, other, isW }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', width: 36, flexShrink: 0 }}>{label}</span>
                <button
                  style={{
                    width: 26, height: 26, flexShrink: 0,
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 6, color: '#fff', fontSize: 16, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                  }}
                  onClick={() => resizeItem(
                    selectedPlaced.id,
                    isW ? Math.max(0.5, val - 0.5) : other,
                    isW ? other : Math.max(0.5, val - 0.5),
                  )}
                >−</button>
                <span style={{ flex: 1, textAlign: 'center', color: '#e2e8f0', fontFamily: 'monospace', fontSize: 12 }}>
                  {val.toFixed(1)} Tiles
                </span>
                <button
                  style={{
                    width: 26, height: 26, flexShrink: 0,
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 6, color: '#fff', fontSize: 16, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                  }}
                  onClick={() => resizeItem(
                    selectedPlaced.id,
                    isW ? val + 0.5 : other,
                    isW ? other : val + 0.5,
                  )}
                >+</button>
              </div>
            ))}

            {/* Rotationssteuerung */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 10 }}>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', width: 36, flexShrink: 0 }}>Winkel</span>
              <button
                style={{
                  width: 26, height: 26, flexShrink: 0,
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6, color: '#fff', fontSize: 14, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title="45° links drehen"
                onClick={() => rotateItem(selectedPlaced.id, ((selectedPlaced.rotation - 45) + 360) % 360)}
              >↺</button>
              <span style={{ flex: 1, textAlign: 'center', color: '#e2e8f0', fontFamily: 'monospace', fontSize: 12 }}>
                {Math.round(selectedPlaced.rotation)}°
              </span>
              <button
                style={{
                  width: 26, height: 26, flexShrink: 0,
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6, color: '#fff', fontSize: 14, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title="45° rechts drehen"
                onClick={() => rotateItem(selectedPlaced.id, (selectedPlaced.rotation + 45) % 360)}
              >↻</button>
            </div>

            <button
              style={btnStyle('rgba(239,68,68,0.75)')}
              onClick={handleDeleteSelected}
            >
              🗑 Möbel entfernen
            </button>
          </div>
        )}

        {/* Upload-Formular */}
        {showUpload ? (
          <>
            <div style={sectionTitle}>PNG hochladen</div>
            <UploadForm onDone={() => setShowUpload(false)} />
          </>
        ) : editingItem ? (
          <EditCatalogForm item={editingItem} onDone={() => setEditingItem(null)} />
        ) : (
          isAuth && (
            <div style={{ padding: '8px 12px 4px' }}>
              <button style={btnStyle('rgba(255,255,255,0.08)')}
                onClick={() => setShowUpload(true)}>
                ➕ Neues Möbel hochladen
              </button>
            </div>
          )
        )}

        {/* Gruppen-Tabs */}
        {!showUpload && !editingItem && (
          <>
            <div style={{
              display: 'flex', gap: 4, flexWrap: 'wrap',
              padding: '8px 12px 4px',
            }}>
              {groups.map((g) => (
                <button key={g} onClick={() => setActiveGroup(g)} style={{
                  background: activeGroup === g ? 'rgba(79,142,247,0.3)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${activeGroup === g ? 'rgba(99,179,237,0.6)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: 12,
                  padding: '3px 10px',
                  color: '#fff',
                  fontSize: 11,
                  cursor: 'pointer',
                }}>
                  {g}
                </button>
              ))}
            </div>

            {/* Katalog-Grid */}
            <div style={sectionTitle}>
              {activeGroup} ({filtered.length})
            </div>
            <div style={gridStyle}>
              {filtered.map((item) => (
                <div key={item.id}
                  style={{ ...tileStyle(pendingCatalogItem?.id === item.id), position: 'relative' }}
                  onClick={() => handleSelectCatalog(item)}
                  title={`${item.name} · ${item.group}`}
                >
                  {isAuth && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingItem(item); }}
                      style={{
                        position: 'absolute', top: 2, right: 2,
                        background: 'rgba(0,0,0,0.55)', border: 'none',
                        borderRadius: 4, color: 'rgba(255,255,255,0.7)',
                        fontSize: 10, cursor: 'pointer', padding: '1px 4px',
                        lineHeight: 1.4,
                      }}
                      title="Bearbeiten"
                    >✏</button>
                  )}
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    style={{ width: '100%', aspectRatio: '1', objectFit: 'contain', borderRadius: 4 }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                    {item.name}
                  </span>
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ gridColumn: '1/-1', color: 'rgba(255,255,255,0.3)', fontSize: 12, padding: '8px 0' }}>
                  Noch keine Möbel in dieser Gruppe.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FurniturePanel;
